/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { RosterDay, Agent, DayOfWeek, Shift } from "../../util/types";
// import * as apiService from "";
// import logger from '../../utils/logger';
// import api
import Spinner from "../../components/Loader";
import apiService from "services/apiService";
import AssignedAgentsModal from "./AssignedAgentsModal";
import ShiftCard from "./ShiftCard";
import SwapFormModal from "./SwapFormModal";

const DAY_ORDER: DayOfWeek[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const mapApiAgentToAgent = (agent: any): Agent => ({
  id: agent.userId,
  agentName: agent.displayName,
  email: agent.email,
  phNumber: agent.phoneNumber,
  gender: agent.gender,
  efficiency: agent.efficiency,
  availability: agent.availability,
  timePreference: agent.timePreference,
});

const getSelectedDaysLabel = (days: DayOfWeek[]) => {
  if (days.length === 7) return "All";
  if (days.length === 0) return "None";
  return days.length;
};

const getGenerateButtonTitle = (hasShiftsConfigured: boolean) => {
  return hasShiftsConfigured
    ? ""
    : "Please configure shifts first in Shift Configuration page";
};

const getDownloadButtonTitle = (rosterGenerated: boolean) => {
  return rosterGenerated
    ? "Download roster as Excel"
    : "Generate roster first to download";
};

const renderAgentCountBadge = (
  rosterGenerated: boolean,
  unassignedCount: number,
  allAgentsCount: number,
) => {
  if (rosterGenerated && unassignedCount > 0) {
    return (
      <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">
        {unassignedCount}
      </span>
    );
  }

  if (!rosterGenerated && allAgentsCount > 0) {
    return (
      <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">
        {allAgentsCount}
      </span>
    );
  }

  return null;
};

const renderAgentsPanelContent = (
  isFetchingAgents: boolean,
  rosterGenerated: boolean,
  unassignedAgentsWithDays: Array<{ agent: Agent; unassignedOnDays: DayOfWeek[] }>,
  allAgents: Agent[],
) => {
  if (isFetchingAgents) {
    return <Spinner />;
  }

  if (rosterGenerated) {
    if (unassignedAgentsWithDays.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="mb-2">✅</div>
          <p className="text-sm text-green-600 font-medium">
            All agents are assigned!
          </p>
        </div>
      );
    }

    return (
      <ul className="space-y-2">
        {unassignedAgentsWithDays.map((item) => (
          <li
            key={item.agent.id}
            className="p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
          >
            <div className="flex items-start justify-between mb-1">
              <div className="font-semibold text-gray-800 dark:text-gray-200 text-sm">
                {item.agent.agentName}
              </div>
              <span className="text-xs bg-red-100 dark:bg-red-800 px-1.5 py-0.5 rounded border border-red-300 dark:border-red-700 text-gray-800 dark:text-gray-200">
                {item.agent.gender}
              </span>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              <span className="font-semibold text-red-600 dark:text-red-400">
                Unassigned:
              </span>{" "}
              {item.unassignedOnDays.join(", ")}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
              {item.agent.email}
            </div>
          </li>
        ))}
      </ul>
    );
  }

  if (allAgents.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center pt-3">
        No agents found.
      </p>
    );
  }

  return (
    <ul className="space-y-1.5">
      {allAgents.map((agent) => (
        <li
          key={agent.id}
          className="p-2 bg-gray-100 dark:bg-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="font-semibold">{agent.agentName}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {agent.email}
              </div>
            </div>
            <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded">
              {agent.gender}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
};

const renderRosterContent = (
  roster: RosterDay[],
  onViewAssignedAgents: (shift: Shift) => void,
) => {
  if (roster.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-700 p-8 rounded-lg flex items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400 text-base">
          Select options and click generate document to begin.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
      {roster.map((day) => (
        <div
          key={day.day}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 flex flex-col"
        >
          <h2 className="text-base font-bold text-center text-indigo-700 dark:text-indigo-400 border-b-2 border-indigo-200 dark:border-indigo-600 pb-1.5 mb-3">
            {day.day}
          </h2>
          <div className="space-y-3 flex-grow">
            {day.shifts.map((shift) => (
              <ShiftCard
                key={shift.shiftName}
                shift={shift}
                onViewAgents={onViewAssignedAgents}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const RosterViewPage: React.FC = () => {
  const [roster, setRoster] = useState<RosterDay[]>([]);
  const [allAgents, setAllAgents] = useState<Agent[]>([]);
  const [rosterGenerated, setRosterGenerated] = useState(false);

  const [isFetchingAgents, setIsFetchingAgents] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isAgentsModalOpen, setIsAgentsModalOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);

  const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
  const [swapSuccess, setSwapSuccess] = useState<{
    agent1Name: string;
    agent1Shift: string;
    agent2Name: string;
    agent2Shift: string;
    day: DayOfWeek;
  } | null>(null);

  const [rosterOptions, setRosterOptions] = useState({
    checkAvailability: true,
    checkPreference: true,
    days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as DayOfWeek[],
  });

  const [unassignedAgentsWithDays, setUnassignedAgentsWithDays] = useState<
    Array<{ agent: Agent; unassignedOnDays: DayOfWeek[] }>
  >([]);

  // Check if shifts are configured
  const [hasShiftsConfigured, setHasShiftsConfigured] = useState(false);
  const [isCheckingShifts, setIsCheckingShifts] = useState(true);

  useEffect(() => {
    const fetchAllAgents = async () => {
      setIsFetchingAgents(true);
      try {
        const agents = await apiService.get("/api/roster-data/getAll");
        const data = agents.data;
        console.log("Fetched agents:", data);

        // Transform API response to match Agent type
        const transformedAgents: Agent[] = data.map(mapApiAgentToAgent);

        setAllAgents(transformedAgents);
      } catch (err) {
        setError(
          err instanceof Error
            ? "Failed to fetch the list of agents."
            : err.message,
        );
      } finally {
        setIsFetchingAgents(false);
      }
    };

    fetchAllAgents();
  }, []);

  // Check if shifts are configured on mount
  useEffect(() => {
    const checkShiftConfiguration = async () => {
      setIsCheckingShifts(true);
      try {
        const response = await apiService.get("/api/roster/shift-config/week");
        const weekConfig = response.data;
        const hasShifts = Object.keys(weekConfig).some(
          (day) => weekConfig[day]?.length > 0,
        );
        setHasShiftsConfigured(hasShifts);
      } catch (err) {
        console.error("Failed to check shift configuration:", err);
        setHasShiftsConfigured(false);
      } finally {
        setIsCheckingShifts(false);
      }
    };

    checkShiftConfiguration();
  }, []);

  // Effect to calculate unique unassigned agents and their available days
  useEffect(() => {
    if (rosterGenerated && roster.length > 0) {
      const unassignedDaysByAgent = new Map<
        number,
        { agent: Agent; days: Set<DayOfWeek> }
      >();

      roster.forEach((day) => {
        day.unassignedAgents.forEach((agent) => {
          if (!unassignedDaysByAgent.has(agent.id)) {
            unassignedDaysByAgent.set(agent.id, { agent, days: new Set() });
          }
          unassignedDaysByAgent.get(agent.id)!.days.add(day.day);
        });
      });

      const agentsWithDays = Array.from(unassignedDaysByAgent.values()).map(
        (entry) => ({
          agent: entry.agent,
          unassignedOnDays: Array.from(entry.days).sort((a, b) =>
            a.localeCompare(b),
          ), // Sort days for consistent display
        }),
      );

      agentsWithDays.sort((a, b) => {
        const nameA = a.agent?.agentName || "";
        const nameB = b.agent?.agentName || "";
        return nameA.localeCompare(nameB);
      });

      setUnassignedAgentsWithDays(agentsWithDays);
    } else {
      setUnassignedAgentsWithDays([]);
    }
  }, [roster, rosterGenerated]);

  const processRosterData = (data: any[]) => {
    const transformedData: RosterDay[] = data.map((day) => ({
      day: day.day,
      shifts: day.shifts.map((shift: any) => ({
        ...shift,
        assignedAgents: shift.assignedAgents.map(mapApiAgentToAgent),
      })),
      unassignedAgents: day.unassignedAgents.map(mapApiAgentToAgent),
    }));

    const orderedDays = DAY_ORDER;
    const sortedData = transformedData.sort(
      (a, b) => orderedDays.indexOf(a.day) - orderedDays.indexOf(b.day),
    );
    setRoster(sortedData);
  };

  const handleDayToggle = (day: DayOfWeek) => {
    setRosterOptions((prev) => {
      const newDays = prev.days.includes(day)
        ? prev.days.filter((d) => d !== day)
        : [...prev.days, day];
      return { ...prev, days: newDays };
    });
  };

  const handleSelectAllDays = () => {
    setRosterOptions((prev) => ({
      ...prev,
      days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as DayOfWeek[],
    }));
  };

  const handleSelectWeekdays = () => {
    setRosterOptions((prev) => ({
      ...prev,
      days: ["Mon", "Tue", "Wed", "Thu", "Fri"] as DayOfWeek[],
    }));
  };

  const handleClearDays = () => {
    setRosterOptions((prev) => ({ ...prev, days: [] }));
  };

  const handleGenerateRoster = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      if (rosterOptions.days.length === 0) {
        setError("Please select at least one day to generate roster.");
        setIsGenerating(false);
        return;
      }

      // Mapping from abbreviated to full day names
      const dayMapping: { [key in DayOfWeek]: string } = {
        Mon: "Monday",
        Tue: "Tuesday",
        Wed: "Wednesday",
        Thu: "Thursday",
        Fri: "Friday",
        Sat: "Saturday",
        Sun: "Sunday",
      };

      // Transform days to full names for API request
      const fullDayNames = rosterOptions.days.map((day) => dayMapping[day]);

      const newRoster = await apiService.post("/api/roster/generate", {
        checkAvailability: rosterOptions.checkAvailability,
        checkPreference: rosterOptions.checkPreference,
        days: fullDayNames,
      });
      console.log("Roster generation response:", newRoster);
      processRosterData(newRoster.data);
      console.log("Generated roster:", newRoster.data);
      setRosterGenerated(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to generate roster.",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleViewAssignedAgents = (shift: Shift) => {
    setSelectedShift(shift);
    setIsAgentsModalOpen(true);
  };

  const handleSwap = async (
    day: DayOfWeek,
    agent1Id: number,
    agent1ShiftName: string,
    agent2Id: number,
    agent2ShiftName: string,
  ) => {
    try {
      setError(null);

      // Find agent names before swap
      const agent1 = allAgents.find((a) => a.id === agent1Id);
      const agent2 = allAgents.find((a) => a.id === agent2Id);

      const updatedRosterDay = await apiService.put(
        "/api/roster/swapAgentShifts",
        {
          day,
          agent1Id,
          agent1ShiftName,
          agent2Id,
          agent2ShiftName,
        },
      );

      const transformedDayData: RosterDay = {
        day: updatedRosterDay.data.day,
        shifts: updatedRosterDay.data.shifts.map((shift: any) => ({
          ...shift,
          assignedAgents: shift.assignedAgents.map(mapApiAgentToAgent),
        })),
        unassignedAgents:
          updatedRosterDay.data.unassignedAgents.map(mapApiAgentToAgent),
      };

      // Update only the specific day in the roster array
      setRoster((prevRoster) =>
        prevRoster.map((rosterDay) =>
          rosterDay.day === day ? transformedDayData : rosterDay,
        ),
      );

      // Show success notification
      setSwapSuccess({
        agent1Name: agent1?.agentName || "Agent 1",
        agent1Shift: agent1ShiftName,
        agent2Name: agent2?.agentName || "Agent 2",
        agent2Shift: agent2ShiftName,
        day,
      });

      // Auto-hide notification after 5 seconds
      setTimeout(() => setSwapSuccess(null), 5000);

      setIsSwapModalOpen(false);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to swap shifts";

      setError(errorMessage);
    }
  };

  const handleDownloadExcel = async () => {
    setIsDownloading(true);
    setError(null);

    try {
      const response = await apiService.get("/api/roster/downloadRosterExcel", {
        responseType: "blob",
      });

      console.log("Download response:", response);

      if (!response.status || response.status !== 200) {
        throw new Error("Failed to download Excel file");
      }

      // Get the blob from the response (it's already a blob when responseType: 'blob')
      const blob = response.data;

      // Verify blob size
      if (blob.size === 0) {
        throw new Error("Downloaded file is empty");
      }

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = `roster_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to download roster as Excel.",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="w-full px-6 py-4 h-page-content">
      {/* Success Notification Banner */}
      {swapSuccess && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/30 border-l-4 border-green-500 dark:border-green-400 p-4 rounded-lg shadow-md animate-fade-in-up">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-500 dark:text-green-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-semibold text-green-800 dark:text-green-200">
                Shift Swap Successful!
              </h3>
              <div className="mt-1 text-sm text-green-700 dark:text-green-300">
                <p className="mb-1">
                  The following shifts have been swapped for{" "}
                  <span className="font-semibold">{swapSuccess.day}</span>:
                </p>
                <div className="flex items-center space-x-2 text-xs">
                  <span className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded">
                    {swapSuccess.agent1Name}: {swapSuccess.agent1Shift} →{" "}
                    {swapSuccess.agent2Shift}
                  </span>
                  <svg
                    className="h-4 w-4 text-green-600 dark:text-green-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                    />
                  </svg>
                  <span className="bg-green-100 dark:bg-green-800 px-2 py-1 rounded">
                    {swapSuccess.agent2Name}: {swapSuccess.agent2Shift} →{" "}
                    {swapSuccess.agent1Shift}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setSwapSuccess(null)}
              className="flex-shrink-0 ml-3 text-green-500 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Weekly Roster</h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center space-x-3 bg-gray-100 dark:bg-gray-700 p-1.5 rounded-lg">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={rosterOptions.checkAvailability}
                onChange={(e) =>
                  setRosterOptions((prev) => ({
                    ...prev,
                    checkAvailability: e.target.checked,
                  }))
                }
                className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-0 focus:ring-transparent focus:outline-none focus:ring-offset-0 focus:border-gray-300 dark:focus:border-gray-600 dark:bg-gray-600 cursor-pointer"
              />
              <span className="ml-1.5 text-xs text-gray-700 dark:text-gray-300">Availability</span>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={rosterOptions.checkPreference}
                onChange={(e) =>
                  setRosterOptions((prev) => ({
                    ...prev,
                    checkPreference: e.target.checked,
                  }))
                }
                className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-0 focus:ring-transparent focus:outline-none focus:ring-offset-0 focus:border-gray-300 dark:focus:border-gray-600 dark:bg-gray-600 cursor-pointer"
              />
              <span className="ml-1.5 text-xs text-gray-700 dark:text-gray-300">Preference</span>
            </div>

            {/* Day Selection Dropdown */}
            <div className="relative group">
              <button
                type="button"
                className="flex items-center space-x-1.5 px-2.5 py-1 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors text-xs"
              >
                <span className="text-gray-700 dark:text-gray-200">
                  Days: {getSelectedDaysLabel(rosterOptions.days)}
                </span>
                <svg
                  className="w-3 h-3 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Dropdown Menu */}
              <div className="absolute left-0 mt-1 w-48 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="p-2">
                  <div className="flex gap-1.5 mb-2 pb-2 border-b dark:border-gray-600">
                    <button
                      onClick={handleSelectAllDays}
                      className="flex-1 px-2 py-1 text-[10px] bg-indigo-50 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900"
                    >
                      All
                    </button>
                    <button
                      onClick={handleSelectWeekdays}
                      className="flex-1 px-2 py-1 text-[10px] bg-indigo-50 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900"
                    >
                      Weekdays
                    </button>
                    <button
                      onClick={handleClearDays}
                      className="flex-1 px-2 py-1 text-[10px] bg-gray-50 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-500"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {(
                      [
                        "Mon",
                        "Tue",
                        "Wed",
                        "Thu",
                        "Fri",
                        "Sat",
                        "Sun",
                      ] as DayOfWeek[]
                    ).map((day) => (
                      <label
                        key={day}
                        className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={rosterOptions.days.includes(day)}
                          onChange={() => handleDayToggle(day)}
                          className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-500 text-indigo-600 focus:ring-0 focus:ring-transparent focus:outline-none focus:ring-offset-0 focus:border-gray-300 dark:focus:border-gray-500 dark:bg-gray-600 cursor-pointer"
                        />
                        <span className="ml-1.5 text-xs text-gray-700 dark:text-gray-200">
                          {day}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={handleGenerateRoster}
            disabled={isGenerating || !hasShiftsConfigured || isCheckingShifts}
            className="bg-indigo-600 text-white font-semibold px-4 py-2 text-sm rounded-lg shadow-md hover:bg-indigo-700 transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center whitespace-nowrap"
            title={getGenerateButtonTitle(hasShiftsConfigured)}
          >
            {isGenerating && (
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {isGenerating ? "Generating..." : "Generate Roster"}
          </button>
          <button
            onClick={handleDownloadExcel}
            disabled={!rosterGenerated || isDownloading}
            className="bg-blue-600 text-white font-semibold px-3 py-1.5 text-sm rounded-lg shadow-md hover:bg-blue-700 transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
            title={getDownloadButtonTitle(rosterGenerated)}
          >
            {isDownloading && (
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            )}
            {!isDownloading && (
              <svg
                className="w-4 h-4 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            )}
            {isDownloading ? "Downloading..." : "Download Roster"}
          </button>
          <button
            onClick={() => setIsSwapModalOpen(true)}
            disabled={!rosterGenerated}
            className="bg-green-600 text-white font-semibold px-3 py-1.5 text-sm rounded-lg shadow-md hover:bg-green-700 transition-colors duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Swap Agents
          </button>
        </div>
      </div>

      {error && (
        <div className="text-red-500 dark:text-red-400 bg-red-100 dark:bg-red-900/30 p-3 rounded-lg mb-3 text-base border border-red-200 dark:border-red-800">
          {error}
        </div>
      )}

      {!hasShiftsConfigured && !isCheckingShifts && (
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200 px-3 py-2 rounded-lg mb-3 flex items-center text-base">
          <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span>
            ⚠️ No shifts configured. Please go to{" "}
            <strong>Shift Configuration</strong> page and configure shifts
            before generating roster.
          </span>
        </div>
      )}

      <div className="flex gap-4">
        {/* Roster Grid container (Left) */}
        <div className="flex-1 min-w-0">
          {renderRosterContent(roster, handleViewAssignedAgents)}
        </div>

        {/* Agents Panel (Right) */}
        <div className="w-80 flex-shrink-0">
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 flex flex-col sticky top-4"
            style={{ maxHeight: "calc(100vh - 200px)" }}
          >
            <div className="flex justify-between items-center border-b dark:border-gray-700 pb-2 mb-3">
              <h3 className="text-base font-bold text-gray-800 dark:text-gray-200">
                {rosterGenerated ? "Unassigned Agents" : "All Agents"}
              </h3>
              {renderAgentCountBadge(
                rosterGenerated,
                unassignedAgentsWithDays.length,
                allAgents.length,
              )}
            </div>
            <div className="flex-1 overflow-y-auto pr-2">
              {renderAgentsPanelContent(
                isFetchingAgents,
                rosterGenerated,
                unassignedAgentsWithDays,
                allAgents,
              )}
            </div>
          </div>
        </div>
      </div>

      <AssignedAgentsModal
        isOpen={isAgentsModalOpen}
        onClose={() => setIsAgentsModalOpen(false)}
        shift={selectedShift}
      />

      <SwapFormModal
        isOpen={isSwapModalOpen}
        onClose={() => setIsSwapModalOpen(false)}
        onSwap={handleSwap}
        roster={roster}
      />
    </div>
  );
};

export default RosterViewPage;